import { findJitenSubtitle } from '@project/common/jiten/reader-events';
import type { JitenTarget } from '@project/common/jiten/jiten';
import { useJitenTarget } from '@project/common/app/hooks/use-jiten-target';
import { readJitenWords } from '@project/common/jiten/reader-words';
import type { MutableRefObject } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { makeStyles } from '@mui/styles';
import type {
    SubtitleModel,
    AudioTrackModel,
    CardTextFieldValues,
    ControlType,
    IndexedSubtitleModel,
    AssSubtitleTrack,
    PlaybackState,
} from '@project/common';
import { PostMineAction, PlayMode, PostMinePlayback } from '@project/common';
import type {
    MiscSettings,
    SubtitleSettings,
    AnkiSettings,
    AsbplayerSettings,
    SubtitleAlignment,
    SettingsProvider,
} from '@project/common/settings';
import {
    changeForTextSubtitleSetting,
    subtitlesWidthCssValue,
    textSubtitleSettingsForTrack,
    PauseOnHoverMode,
    allTextSubtitleSettings,
    embeddedTrackLanguageDisplayName,
    embeddedTrackLanguages,
} from '@project/common/settings';
import {
    adjacentSubtitle,
    arrayEquals,
    compareSubtitlesForDisplay,
    surroundingSubtitles,
    mockSurroundingSubtitles,
    seekWithNudge,
    surroundingSubtitlesAroundInterval,
    subtitleTimestampWithDelay,
    errorMessageFromVideo,
    formatAsSignedMs,
    mapSubtitlesForDisplay,
    timeDurationDisplay,
} from '@project/common/util';
import { asbInfo, asbTrace, asbWarn, asbError } from '@project/common/util/log';
import { ASB_SUBTITLE_INDEX_ATTRIBUTE } from '@project/common/annotations';
import Clock from '@project/common/playback/timing/clock';
import {
    formatPlaybackModeNotifications,
    playbackModeNotificationJoin,
} from '@project/common/playback/controllers/playback-mode-controller';
import type {
    PlaybackModeNotificationFormatOptions,
    PlayModeTransition,
} from '@project/common/playback/controllers/playback-mode-controller';
import PlaybackEngine from '@project/common/playback/playback-engine';
import VideoFrameTimingDriver from '@project/common/playback/timing/video-frame-timing-driver';
import type { Point } from '@project/common/app/components/Controls';
import Controls from '@project/common/app/components/Controls';
import PlayerChannel from '@project/common/app/services/player-channel';
import type ChromeExtension from '@project/common/app/services/chrome-extension';
import Alert from '@project/common/app/components/Alert';
import AssSubtitles from '@project/common/app/components/AssSubtitles';
import type { AlertNotification } from '@project/common/app/components/Alert';
import Button from '@mui/material/Button';
import { useJitenSubtitleElement, useJitenSubtitleSource } from '@project/common/app/hooks/use-jiten-subtitles';
import { useAppKeyBinder } from '@project/common/app/hooks/use-app-key-binder';
import type { Direction } from '@project/common/app/hooks/use-swipe';
import { useSwipe } from '@project/common/app/hooks/use-swipe';
import '@project/common/app/components/subtitles.css';
import i18n from 'i18next';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { usePlaybackPreferences } from '@project/common/app/hooks/use-playback-preferences';
import type { MiningContext } from '@project/common/app/services/mining-context';
import useSnackbar from '@project/common/hooks/use-snackbar';
import { useSubtitleStyles } from '@project/common/app/hooks/use-subtitle-styles';
import { useFullscreen } from '@project/common/app/hooks/use-fullscreen';
import MobileVideoOverlay from '@project/common/components/MobileVideoOverlay';
import BlurOverlay from '@project/common/app/components/BlurOverlay';
import { CachedLocalStorage } from '@project/common/app/services/cached-local-storage';
import useLastScrollableControlType from '@project/common/hooks/use-last-scrollable-control-type';
import { openMatroskaAudio } from '@project/common/media-reader/matroska-reader';
import type { Theme } from '@mui/material/styles';

type MatroskaAudio = Awaited<ReturnType<typeof openMatroskaAudio>> & { selectedTrack: string };
interface ExperimentalHTMLVideoElement extends HTMLVideoElement {
    readonly audioTracks: any;
}

const useStyles = makeStyles<Theme>(() => ({
    root: {
        position: 'relative',
        backgroundColor: 'black',
        height: '100dvh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    video: {
        margin: 'auto',
        width: '100%',
        height: '100%',
        minHeight: 0,
        objectFit: 'contain',
    },
    cursorHidden: {
        cursor: 'none',
    },
    mobileOverlay: {
        '&&': {
            display: 'flex',
            width: 'max-content',
            margin: '0 auto',
            backgroundColor: 'transparent',
        },
    },
}));

const showingSubtitleHtml = (
    subtitle: IndexedSubtitleModel,
    videoRef: MutableRefObject<ExperimentalHTMLVideoElement | undefined>,
    subtitleStyles: string,
    subtitleClasses: string,
    imageBasedSubtitleScaleFactor: number
) => {
    if (subtitle.textImage) {
        const imageScale =
            (imageBasedSubtitleScaleFactor * (videoRef.current?.clientWidth ?? window.screen.availWidth)) /
            subtitle.textImage.screen.width;
        const width = imageScale * subtitle.textImage.image.width;
        return `
<div style="max-width:${width}px;margin:auto;">
<img
    style="width:100%;"
    alt="subtitle"
    src="${subtitle.textImage.dataUrl}"
    class="${subtitleClasses}"
/>
</div>
`;
    }
    const allSubtitleClasses = subtitleClasses ? `${subtitleClasses} asbplayer-subtitles` : 'asbplayer-subtitles';
    return `<span class="${allSubtitleClasses}" style="${subtitleStyles}" data-track="${subtitle.track}" ${ASB_SUBTITLE_INDEX_ATTRIBUTE}="${subtitle.index}">${subtitle.text}</span>`;
};

interface ShowingSubtitleProps {
    subtitle: IndexedSubtitleModel;
    visible: boolean;
    renderHtml: (subtitle: IndexedSubtitleModel) => string;
    className?: string;
    onMouseOver: React.MouseEventHandler<HTMLDivElement>;
    onMouseOut: React.MouseEventHandler<HTMLDivElement>;
}

const ShowingSubtitle = React.memo(function ShowingSubtitle({
    subtitle,
    visible,
    renderHtml,
    className,
    onMouseOver,
    onMouseOut,
}: ShowingSubtitleProps) {
    const ref = useJitenSubtitleElement(subtitle.index, renderHtml(subtitle));
    return (
        <div
            className={className ? className : ''}
            aria-hidden={!visible}
            style={visible ? { pointerEvents: 'auto' } : { visibility: 'hidden', pointerEvents: 'none' }}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            ref={ref}
        />
    );
});

const useSubtitleContainerStyles = makeStyles(() => ({
    subtitleContainer: {
        position: 'absolute',
        zIndex: 6,
        maxWidth: '100%',
        paddingLeft: 20,
        paddingRight: 20,
        textAlign: 'center',
        whiteSpace: 'normal',
        lineHeight: 'inherit',
    },
}));

interface SubtitleContainerProps {
    subtitleSettings: SubtitleSettings;
    alignment: SubtitleAlignment;
    subtitleZIndex: boolean;
    baseOffset: number;
    children: React.ReactNode;
}

const SubtitleContainer = React.forwardRef<HTMLDivElement, SubtitleContainerProps>(function SubtitleContainer(
    { subtitleSettings, alignment, baseOffset, children, subtitleZIndex }: SubtitleContainerProps,
    ref
) {
    const classes = useSubtitleContainerStyles();
    const width = subtitlesWidthCssValue(subtitleSettings);
    return (
        <div
            ref={ref}
            className={classes.subtitleContainer}
            style={{
                ...(alignment === 'bottom'
                    ? { bottom: subtitleSettings.subtitlePositionOffset + baseOffset }
                    : { top: subtitleSettings.topSubtitlePositionOffset + baseOffset }),
                ...(width === undefined ? {} : { width }),
                zIndex: subtitleZIndex ? 12 : 0,
                pointerEvents: 'none',
            }}
        >
            {children}
        </div>
    );
});

export interface SeekRequest {
    timestamp: number;
}

interface Props {
    embedded?: boolean;
    playerSubtitles?: IndexedSubtitleModel[];
    assTracks?: readonly AssSubtitleTrack[];
    topControls?: (controls: React.ReactNode) => React.ReactNode;
    onOpenCopyHistory?: () => void;
    subtitleExtractionProgress?: number;
    onCancelSubtitleExtraction?: () => void;
    disableKeyEvents?: boolean;
    settings: AsbplayerSettings;
    settingsProvider: SettingsProvider;
    extension: ChromeExtension;
    videoFile: string;
    channel: string;
    popOut: boolean;
    miningContext: MiningContext;
    ankiDialogOpen: boolean;
    seekRequest?: SeekRequest;
    onAnkiDialogRequest: (
        videoFileUrl: string,
        videoFileName: string,
        selectedAudioTrack: string | undefined,
        playbackRate: number,
        subtitle: SubtitleModel,
        surroundingSubtitles: SubtitleModel[],
        cardTextFieldValues: CardTextFieldValues,
        timestamp: number
    ) => void;
    onSettingsChanged: (settings: Partial<AsbplayerSettings>) => void;
    profile?: string;
    onAnkiDialogRewind: () => void;
    onError: (error: string) => void;
}

interface MinedRecord {
    videoFileUrl: string;
    videoFileName: string;
    selectedAudioTrack: string | undefined;
    playbackRate: number;
    subtitle: SubtitleModel;
    surroundingSubtitles: SubtitleModel[];
    timestamp: number;
}

const allSubtitleAlignments = (subtitleSettings: SubtitleSettings) => {
    return allTextSubtitleSettings(subtitleSettings).map((s) => s.subtitleAlignment);
};

const lastControlTypeKey = 'lastScrollableControlType';
const storage = new CachedLocalStorage();

const fetchLastControlType = async (): Promise<ControlType | undefined> => {
    const val = storage.get(lastControlTypeKey);

    if (val == null) {
        return undefined;
    }

    return parseInt(val);
};

const saveLastControlType = async (controlType: ControlType): Promise<void> => {
    storage.set(lastControlTypeKey, String(controlType));
};

function VideoPlayer({
    embedded = false,
    playerSubtitles,
    assTracks: playerAssTracks,
    topControls,
    onOpenCopyHistory,
    subtitleExtractionProgress,
    onCancelSubtitleExtraction,
    disableKeyEvents = false,
    settings,
    settingsProvider,
    extension,
    videoFile,
    channel,
    popOut,
    miningContext,
    ankiDialogOpen,
    seekRequest,
    onAnkiDialogRequest,
    onError,
    onAnkiDialogRewind,
    onSettingsChanged,
    profile,
}: Props) {
    const classes = useStyles();
    const { t } = useTranslation();
    const poppingInRef = useRef<boolean>(undefined);
    const onSettingsChangedRef = useRef(onSettingsChanged);
    onSettingsChangedRef.current = onSettingsChanged;
    const settingsRef = useRef(settings);
    settingsRef.current = settings;
    const playbackPreferences = usePlaybackPreferences();
    const videoRef = useRef<ExperimentalHTMLVideoElement>(undefined);
    const volumeGraph = useRef<{
        context: AudioContext;
        source: MediaElementAudioSourceNode;
        gain: GainNode;
        element: HTMLVideoElement;
    }>(undefined);
    const requestedVolume = useRef(1);

    useEffect(() => {
        const resume = () => {
            const context = volumeGraph.current?.context;
            if (context?.state === 'suspended')
                void context.resume().catch((error) => asbError('playback/audio', error));
        };
        document.addEventListener('pointerdown', resume);
        document.addEventListener('keydown', resume);
        return () => {
            document.removeEventListener('pointerdown', resume);
            document.removeEventListener('keydown', resume);
            // React can detach and reattach the same video during development.
            queueMicrotask(() => {
                if (videoRef.current) return;
                const graph = volumeGraph.current;
                graph?.source.disconnect();
                graph?.gain.disconnect();
                void graph?.context.close();
                volumeGraph.current = undefined;
            });
        };
    }, []);

    const [video, setVideo] = useState<ExperimentalHTMLVideoElement>();
    const playbackEngineRef = useRef<PlaybackEngine<IndexedSubtitleModel>>(undefined);
    const decodeRecoveryAttemptedRef = useRef(false);
    const profileRef = useRef(profile);
    const hiddenVideoRef = useRef<HTMLVideoElement | null>(null); // seek preview thumbnail
    const [hiddenVideoReady, setHiddenVideoReady] = useState(false);
    const hiddenVideoRefCallback = useCallback((node: HTMLVideoElement | null) => {
        hiddenVideoRef.current = node;
        setHiddenVideoReady(node !== null);
    }, []);
    const playerChannel = useMemo(() => {
        asbTrace('playback/video-player', 'Creating player channel');
        return new PlayerChannel(channel);
    }, [channel]);
    const [playerChannelSubscribed, setPlayerChannelSubscribed] = useState<boolean>(false);
    const { fullscreen, requestFullscreen } = useFullscreen();
    const playing = () => !videoRef.current?.paused || false;
    const [lengthMs, setLengthMs] = useState<number>(0);
    const lengthMsRef = useRef(lengthMs);
    lengthMsRef.current = lengthMs;
    const [videoFileName, setVideoFileName] = useState<string>();
    const videoFileNameRef = useRef(videoFileName);
    videoFileNameRef.current = videoFileName;
    const [videoWidth, setVideoWidth] = useState<number>(); // width and height are original width and height from metadata
    const [videoHeight, setVideoHeight] = useState<number>();
    const [offset, setOffset] = useState<number>(0);
    const offsetRef = useRef(offset);
    offsetRef.current = offset;
    const [audioTracks, setAudioTracks] = useState<AudioTrackModel[]>();
    const [selectedAudioTrack, setSelectedAudioTrack] = useState<string>();
    const [sourceFile, setSourceFile] = useState<File>();
    const matroskaAudioRef = useRef<MatroskaAudio>(undefined);
    const autoSelectedAudioFileRef = useRef<File>(undefined);
    const audioSourceUrlRef = useRef<string>(undefined);
    const pendingAudioSelectionRef = useRef<{
        cancel: () => void;
        timestamp: number;
        rate: number;
        resume: boolean;
    }>(undefined);
    const [wasPlayingOnAnkiDialogRequest, setWasPlayingOnAnkiDialogRequest] = useState<boolean>(false);
    const [channelSubtitles, setChannelSubtitles] = useState<IndexedSubtitleModel[]>([]);
    const [channelAssTracks, setChannelAssTracks] = useState<readonly AssSubtitleTrack[]>([]);
    const assTracks = embedded ? (playerAssTracks ?? channelAssTracks) : channelAssTracks;
    const [assRenderingFailed, setAssRenderingFailed] = useState(false);
    useEffect(() => setAssRenderingFailed(false), [assTracks, settings.assSubtitleStyles]);
    const handleAssError = useCallback(
        (error: string) => {
            setAssRenderingFailed(true);
            onError(error);
        },
        [onError]
    );
    const styledAssTracks = settings.assSubtitleStyles && !assRenderingFailed ? assTracks : [];
    const subtitles = embedded && playerSubtitles !== undefined ? playerSubtitles : channelSubtitles;
    const subtitlesRef = useRef(subtitles);
    subtitlesRef.current = subtitles;
    const [showSubtitles, setShowSubtitles] = useState<IndexedSubtitleModel[]>([]);
    const [invisibleSubtitles, setInvisibleSubtitles] = useState<IndexedSubtitleModel[]>([]);
    const [miscSettings, setMiscSettings] = useState<MiscSettings>(settings);
    const miscSettingsRef = useRef(miscSettings);
    miscSettingsRef.current = miscSettings;
    const [subtitleSettings, setSubtitleSettings] = useState<SubtitleSettings>(settings);
    const [ankiSettings, setAnkiSettings] = useState<AnkiSettings>(settings);
    const [displaySubtitles, setDisplaySubtitles] = useState(playbackPreferences.displaySubtitles);
    const [disabledSubtitleTracks, setDisabledSubtitleTracks] = useState<{ [index: number]: boolean }>({});
    const disabledSubtitleTracksRef = useRef(disabledSubtitleTracks);
    disabledSubtitleTracksRef.current = disabledSubtitleTracks;
    useEffect(() => {
        asbTrace('playback/video-player', 'Subtitle display state', { displaySubtitles, disabledSubtitleTracks });
    }, [displaySubtitles, disabledSubtitleTracks]);
    const [playModes, setPlayModes] = useState<Set<PlayMode>>(() => new Set([PlayMode.normal]));
    const synchronizePlaybackModes = useCallback(
        (modes: ReadonlySet<PlayMode>) => {
            const synchronizedModes = new Set(modes);
            setPlayModes(synchronizedModes);
            playerChannel.playModes(synchronizedModes);
        },
        [playerChannel]
    );
    const [subtitlePlayerHidden, setSubtitlePlayerHidden] = useState<boolean>(false);
    const [subtitleAlignments, setSubtitleAlignments] = useState<SubtitleAlignment[]>(
        allSubtitleAlignments(subtitleSettings)
    );
    const [, setBottomSubtitlePositionOffset] = useState<number>(subtitleSettings.subtitlePositionOffset);
    const [, setTopSubtitlePositionOffset] = useState<number>(subtitleSettings.topSubtitlePositionOffset);
    const showSubtitlesRef = useRef<IndexedSubtitleModel[]>([]);
    showSubtitlesRef.current = showSubtitles;
    const invisibleSubtitlesRef = useRef<IndexedSubtitleModel[]>([]);
    invisibleSubtitlesRef.current = invisibleSubtitles;
    const showingSubtitleIndexesRef = useRef<readonly number[]>([]);
    const invisibleSubtitleIndexesRef = useRef<readonly number[]>([]);
    const playbackStateChangedRef = useRef<(state: PlaybackState) => void>(() => {});
    const clock = useMemo<Clock>(() => new Clock(() => performance.now()), []);
    const mousePositionRef = useRef<Point | undefined>(undefined);
    const [showCursor, setShowCursor] = useState<boolean>(isMobile);
    const [controlInsets, setControlInsets] = useState({ top: 0, bottom: 0 });
    const lastMouseMovementTimestamp = useRef<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const [alert, setAlert] = useState<{ open: boolean; notifications: AlertNotification[] }>({
        open: false,
        notifications: [],
    });
    const [alertDisableAutoHide, setAlertDisableAutoHide] = useState<boolean>(false);
    const [pendingPlaybackPosition, setPendingPlaybackPosition] = useState<number>();
    const resumePlaybackSnackbar = useSnackbar({
        open: pendingPlaybackPosition !== undefined,
        onClose: () => playbackEngineRef.current?.dismissPlaybackPosition(),
    });
    const [lastMinedRecord, setLastMinedRecord] = useState<MinedRecord>();
    const [trackCount, setTrackCount] = useState<number>(0);
    const [, forceRender] = useState<any>();
    useEffect(() => {
        setTrackCount(subtitles.reduce((count, subtitle) => Math.max(count, subtitle.track + 1), 0));
    }, [subtitles]);
    const [mineIntervalStartTimestamp, setMineIntervalStartTimestamp] = useState<number>();
    const [blurOverlayVisible, setBlurOverlayVisible] = useState<boolean>(false);
    const handleBlurOverlayToggle = useCallback(() => setBlurOverlayVisible((v) => !v), []);
    const hoveredSubtitleRef = useRef<HTMLElement>(undefined);
    const hoveredSubtitleBoundsRef = useRef<DOMRect>(undefined);
    const thumbnailsRef = useRef<Map<number, string>>(new Map()); // cache thumbnails, in intervals of 5s
    const isGeneratingRef = useRef(false); // avoid subsequent calls to generate thumbnail while generating one

    useEffect(() => {
        setMiscSettings(settings);
        setSubtitleSettings(settings);
        setAnkiSettings(settings);
    }, [settings]);

    useEffect(() => {
        setSubtitleAlignments(allSubtitleAlignments(subtitleSettings));
        setBottomSubtitlePositionOffset(subtitleSettings.subtitlePositionOffset);
        setTopSubtitlePositionOffset(subtitleSettings.topSubtitlePositionOffset);
    }, [subtitleSettings]);

    const keyBinder = useAppKeyBinder(miscSettings.keyBindSet, extension);
    const disableKeyEventsRef = useRef(disableKeyEvents);
    disableKeyEventsRef.current = disableKeyEvents;
    const keyEventsDisabled = useCallback(() => disableKeyEventsRef.current, []);

    useEffect(() => {
        if (i18n.language !== miscSettings.language) {
            void i18n.changeLanguage(miscSettings.language);
        }
    }, [miscSettings]);

    const updatePlayerState = useCallback(() => {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        if (video.paused) {
            playerChannel.pause(false);
        } else {
            playerChannel.play(false);
        }

        playerChannel.playbackRate(video.playbackRate, false);
        playerChannel.currentTime(video.currentTime, false);
        forceRender({});

        if (!video.paused) {
            isPausedDueToHoverRef.current = false;
        }
    }, [playerChannel]);

    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    const notifyPlaybackRate = useCallback(
        (options: ReturnType<PlaybackEngine<IndexedSubtitleModel>['playbackRateChanged']>) => {
            if (!options?.notify) return;
            setAlert({
                open: true,
                notifications: [
                    {
                        key: options.notification.key,
                        message: {
                            locKey: options.notification.locKey,
                            replacements: options.notification.replacements,
                        },
                        severity: 'info',
                    },
                ],
            });
        },
        []
    );

    const updatePlaybackRate = useCallback(
        (playbackRate: number, forwardToPlayer: boolean) => {
            if (forwardToPlayer) playerChannel.playbackRate(playbackRate);
            const playbackEngine = playbackEngineRef.current;
            if (!playbackEngine) return;

            const result = playbackEngine.playbackRateChanged(playbackRate);
            if (result === undefined) return;
            clock.rate = result.playbackRate;
            notifyPlaybackRate(result);
        },
        [clock, notifyPlaybackRate, playerChannel]
    );
    const synchronizePlaybackModesRef = useRef(synchronizePlaybackModes);
    synchronizePlaybackModesRef.current = synchronizePlaybackModes;

    const handlePlaybackModesChanged = useCallback(
        (transition: PlayModeTransition, options: PlaybackModeNotificationFormatOptions = {}): AlertNotification[] => {
            synchronizePlaybackModesRef.current(transition.modes);
            if (!transition.added.size && !transition.removed.size) return [];
            return formatPlaybackModeNotifications(transition, options).map(({ key, text }) => ({
                key,
                message: text,
                severity: 'info',
            }));
        },
        []
    );

    const handlePlaybackRateChanged = useCallback(
        (playbackRate: number) => updatePlaybackRate(playbackRate, false),
        [updatePlaybackRate]
    );
    const handleDurationChanged = useCallback(
        (durationMs: number) => {
            setLengthMs(durationMs);
            playerChannel.duration(durationMs / 1000);
            playbackEngineRef.current?.durationChanged(durationMs);
        },
        [playerChannel]
    );

    const notifyVideoReady = useCallback(
        (element: ExperimentalHTMLVideoElement) => {
            const matroskaAudio = matroskaAudioRef.current;
            const loadAudioTracks = settingsRef.current.loadEmbeddedAudio;
            const preferredLanguage =
                settingsRef.current.rememberEmbeddedLanguage && settingsRef.current.loadPreferredEmbeddedLanguageOnly
                    ? settingsRef.current.embeddedLanguage
                    : undefined;

            if (popOut && window.outerWidth && element.videoWidth > 0 && element.videoHeight > 0) {
                const availWidth = window.screen.availWidth - (window.outerWidth - window.innerWidth);
                const availHeight = window.screen.availHeight - (window.outerHeight - window.innerHeight);
                const resizeRatio = Math.min(
                    1,
                    Math.min(availWidth / element.videoWidth, availHeight / element.videoHeight)
                );

                window.resizeTo(
                    resizeRatio * element.videoWidth + (window.outerWidth - window.innerWidth),
                    resizeRatio * element.videoHeight + (window.outerHeight - window.innerHeight)
                );
            }

            let tracks: AudioTrackModel[] | undefined;
            let selectedTrack: string | undefined;

            if (element.audioTracks) {
                tracks = [];

                for (const t of element.audioTracks) {
                    tracks.push({
                        id: t.id,
                        label: t.label,
                        language: t.language,
                    });

                    if (t.enabled) {
                        selectedTrack = t.id;
                    }
                }
            } else {
                tracks = undefined;
                selectedTrack = undefined;
            }

            if (matroskaAudio) {
                tracks = matroskaAudio.tracks.map((track) => ({
                    ...track,
                    language: embeddedTrackLanguageDisplayName(track.language),
                }));
                selectedTrack = matroskaAudio.selectedTrack;
            }
            if (!loadAudioTracks) tracks = undefined;
            if (preferredLanguage !== undefined)
                tracks = tracks?.filter(
                    (track) =>
                        embeddedTrackLanguageDisplayName(track.language) ===
                        embeddedTrackLanguageDisplayName(preferredLanguage)
                );
            setAudioTracks(tracks);
            setSelectedAudioTrack(selectedTrack);
            asbTrace('playback/video-player', 'Video media is ready', {
                durationMs: element.duration * 1000,
                currentTimeMs: element.currentTime * 1000,
                paused: element.paused,
                playbackRate: element.playbackRate,
                readyState: element.readyState,
                videoWidth: element.videoWidth,
                videoHeight: element.videoHeight,
                audioTrackCount: tracks?.length ?? 0,
                hasSelectedAudioTrack: selectedTrack !== undefined,
            });
            playerChannel.ready(
                element.duration,
                element.paused,
                element.playbackRate,
                tracks,
                selectedTrack,
                element.currentTime
            );
        },
        [playerChannel, popOut]
    );

    const videoRefCallback = useCallback(
        (element: HTMLVideoElement | null) => {
            if (!element) {
                asbTrace('playback/video-player', 'Detached video element');
                videoRef.current = undefined;
                setVideo(undefined);
                return;
            }
            if (element === videoRef.current) return;

            const videoElement = element as ExperimentalHTMLVideoElement;
            asbTrace('playback/video-player', 'Attached video element', {
                readyState: videoElement.readyState,
                currentTimeMs: videoElement.currentTime * 1000,
                paused: videoElement.paused,
                videoWidth: videoElement.videoWidth,
                videoHeight: videoElement.videoHeight,
            });
            videoRef.current = videoElement;
            setVideo(videoElement);
            clock.setTime(videoElement.currentTime * 1000, { paused: videoElement.paused });

            if (videoElement.readyState === 4) {
                notifyVideoReady(videoElement);
                setVideoWidth(videoElement.videoWidth);
                setVideoHeight(videoElement.videoHeight);
            } else {
                videoElement.onloadeddata = () => {
                    notifyVideoReady(videoElement);
                    setVideoWidth(videoElement.videoWidth);
                    setVideoHeight(videoElement.videoHeight);
                };
                videoElement.ondurationchange = () => notifyVideoReady(videoElement);
            }

            videoElement.oncanplay = () => {
                asbTrace('playback/video-player', 'Video became playable', {
                    readyState: videoElement.readyState,
                    currentTimeMs: videoElement.currentTime * 1000,
                });
                playerChannel.readyState(4);
                clock.setTime(videoElement.currentTime * 1000, { paused: videoElement.paused });
            };

            if (isMobile) videoElement.volume = 1; // Force volume to 1 on mobile - users can control device volume
        },
        [clock, playerChannel, notifyVideoReady]
    );

    const updateSubtitlesWithOffset = useCallback((offset: number, notifyPlayer: boolean, notificationKey: string) => {
        const previousOffset = offsetRef.current;
        offsetRef.current = offset;
        setOffset(offset);
        if (notifyPlayer) {
            setAlert({
                open: true,
                notifications: [{ key: notificationKey, message: formatAsSignedMs(offset), severity: 'info' }],
            });
        }
        if (offset === previousOffset) return;

        const shiftedSubtitles = subtitlesRef.current.map((s, i) => ({
            ...s,
            start: s.originalStart + offset,
            end: s.originalEnd + offset,
            index: i,
        }));
        subtitlesRef.current = shiftedSubtitles;
        setChannelSubtitles(shiftedSubtitles);
    }, []);

    useEffect(() => {
        if (!video) return;

        asbTrace('playback/video-player', 'Creating video playback owner', {
            subtitleCount: subtitlesRef.current.length,
            hasVideoFileName: videoFileNameRef.current !== undefined,
            appIntegration: extension.supportsAppIntegration,
        });
        let wasPlaying = !video.paused;
        let recoveringDecodeError = false;
        const playbackEngine = new PlaybackEngine({
            settingsProvider,
            appIntegration: extension.supportsAppIntegration,
            autoPauseCorrectionDisabled: false,
            subtitles: subtitlesRef.current,
            playbackModesDisabled: false,
            playbackModesSuppressed: false,
            playbackPositionKeys: videoFileNameRef.current ? [videoFileNameRef.current] : [],
            timingDriver: new VideoFrameTimingDriver(
                {
                    paused: () => video.paused,
                    playbackRate: () => video.playbackRate,
                    durationMs: () => video.duration * 1000,
                    currentTimeMs: () => video.currentTime * 1000,
                    hasVideoTrack: () =>
                        video.readyState >= HTMLMediaElement.HAVE_METADATA &&
                        video.videoWidth > 0 &&
                        video.videoHeight > 0,
                    frameTimestampMs: () => undefined,
                    externalSeekEvents: false,
                    requestVideoFrameCallback: (callback) => video.requestVideoFrameCallback(callback),
                    cancelVideoFrameCallback: (handle) => video.cancelVideoFrameCallback(handle),
                    addEventListener: (type, listener) => video.addEventListener(type, listener),
                    removeEventListener: (type, listener) => video.removeEventListener(type, listener),
                },
                {
                    onPlay: () => {
                        wasPlaying = true;
                        clock.start();
                        updatePlayerState();
                    },
                    onPause: () => {
                        if (!video.error) wasPlaying = false;
                        clock.stop();
                        updatePlayerState();
                    },
                    onSeeked: (timestampMs) => {
                        clock.setTime(timestampMs, { paused: video.paused }); // rVFC may not run during pause
                        updatePlayerState();
                        if (recoveringDecodeError) {
                            recoveringDecodeError = false;
                            asbInfo('playback/video-player', 'Recovered video after a decode error', { timestampMs });
                        }
                    },
                    onPlaybackRateChanged: handlePlaybackRateChanged,
                    onDurationChanged: handleDurationChanged,
                    onError: () => {
                        const error = errorMessageFromVideo(video);
                        asbTrace('playback/video-player', 'Observed video error', { error });
                        if (video.error?.code === MediaError.MEDIA_ERR_DECODE && !decodeRecoveryAttemptedRef.current) {
                            decodeRecoveryAttemptedRef.current = true;
                            recoveringDecodeError = true;
                            const timestamp = video.currentTime;
                            const playbackRate = video.playbackRate;
                            const resumePlayback = wasPlaying;
                            asbWarn('playback/video-player', 'Reloading video to recover from a decode error', {
                                error,
                                timestampMs: timestamp * 1000,
                                playbackRate,
                                resumePlayback,
                            });
                            const audioTrack = [...(video.audioTracks ?? [])].find((track) => track.enabled)?.id;
                            if (audioTrack !== undefined) {
                                video.addEventListener(
                                    'loadedmetadata',
                                    () => {
                                        void selectAudioTrack(audioTrack).catch(onErrorRef.current);
                                    },
                                    {
                                        once: true,
                                    }
                                );
                            }
                            // Reloading clears a failed decoder without changing the requested position.
                            video.load();
                            video.currentTime = timestamp;
                            video.playbackRate = playbackRate;
                            if (resumePlayback) {
                                void video.play().catch((error) => onErrorRef.current?.(String(error)));
                            }
                            return;
                        }
                        onErrorRef.current?.(error);
                    },
                }
            ),
            callbacks: {
                pause: () => {
                    video.pause();
                    clock.stop();
                },
                play: async () => {
                    await video.play();
                    clock.start();
                },
                seek: async (timestampMs) => {
                    decodeRecoveryAttemptedRef.current = false;
                    video.currentTime = timestampMs / 1000;
                    clock.setTime(timestampMs, { paused: video.paused });
                },
                setPlaybackRate: (playbackRate) => {
                    if (video.playbackRate !== playbackRate) video.playbackRate = playbackRate;
                },
                setSubtitleOffset: (offset, options, notificationKey) =>
                    updateSubtitlesWithOffset(offset, options.notifyPlayer, notificationKey),
                playbackStateChanged: (state) => {
                    playbackStateChangedRef.current(state);
                    playerChannel.playbackState(state);
                },
                playbackPositionChanged: setPendingPlaybackPosition,
                saveSettings: (settings) => onSettingsChangedRef.current(settings),
                playbackModesChanged: (transition) => {
                    const notifications = handlePlaybackModesChanged(transition);
                    if (notifications.length) setAlert({ open: true, notifications });
                },
                initialPlaybackSettingsChanged: (settings) => {
                    asbTrace('playback/video-player', 'Applying initial video playback settings', {
                        subtitleOffset: settings.subtitleOffset,
                        playbackRate: settings.playbackRate,
                        playbackModes: [...settings.playbackModeTransition.modes],
                    });
                    playerChannel.offset(settings.subtitleOffset);
                    clock.rate = settings.playbackRate;
                    playerChannel.playbackRate(settings.playbackRate, false);

                    const offsetAndRate = (_t: TFunction) =>
                        settings.notifications.offsetAndRate
                            .map((notification) =>
                                notification.type === 'message'
                                    ? notification.message
                                    : _t(notification.notification.locKey, notification.notification.replacements)
                            )
                            .join(playbackModeNotificationJoin);
                    const playbackModeNotifications = handlePlaybackModesChanged(settings.playbackModeTransition, {
                        includeTransition: false,
                    });
                    const notifications: AlertNotification[] = [];
                    if (offsetAndRate.length) {
                        notifications.push({
                            message: offsetAndRate,
                            severity: 'info',
                            autoHideDuration: settings.autoHideDuration,
                        });
                    }
                    notifications.push(
                        ...playbackModeNotifications.map((notification) => ({
                            ...notification,
                            autoHideDuration: settings.autoHideDuration,
                        }))
                    );
                    if (notifications.length) setAlert({ open: true, notifications });
                },
                onError: (error) => {
                    asbTrace('playback/video-player', 'Video playback owner reported an error', { error });
                    onErrorRef.current?.(String(error));
                },
            },
        });

        playbackEngineRef.current = playbackEngine;
        asbTrace('playback/video-player', 'Binding video playback owner');
        playbackEngine.bind();

        return () => {
            asbTrace('playback/video-player', 'Unbinding video playback owner');
            playbackEngine.unbind();
            if (playbackEngineRef.current === playbackEngine) {
                playbackEngineRef.current = undefined;
            }
        };
    }, [
        clock,
        extension,
        handleDurationChanged,
        handlePlaybackRateChanged,
        handlePlaybackModesChanged,
        playerChannel,
        settingsProvider,
        updatePlayerState,
        updateSubtitlesWithOffset,
        video,
    ]);

    useEffect(() => {
        if (profileRef.current === profile) return;
        profileRef.current = profile;
        playbackEngineRef.current?.profileChanged(profile);
    }, [profile]);

    useEffect(() => {
        if (!video || !sourceFile) return;
        autoSelectedAudioFileRef.current = undefined;
        if (!/\.(mkv|webm)$/i.test(sourceFile.name)) {
            notifyVideoReady(video);
            return;
        }
        if (!settings.loadEmbeddedAudio || matroskaAudioRef.current) {
            notifyVideoReady(video);
            return;
        }
        if (video.audioTracks?.length > 1) {
            notifyVideoReady(video);
            return;
        }
        let disposed = false;
        void openMatroskaAudio(sourceFile)
            .then((source) => {
                if (disposed || source.tracks.length === 0) return;
                matroskaAudioRef.current = {
                    ...source,
                    selectedTrack: source.tracks[0].id,
                };
                notifyVideoReady(video);
            })
            .catch((error) => asbWarn('playback/audio', 'Failed to read Matroska audio tracks', error));
        return () => {
            disposed = true;
        };
    }, [
        sourceFile,
        video,
        notifyVideoReady,
        settings.loadEmbeddedAudio,
        settings.rememberEmbeddedLanguage,
        settings.loadPreferredEmbeddedLanguageOnly,
        settings.embeddedLanguage,
    ]);

    useEffect(() => {
        return () => {
            pendingAudioSelectionRef.current?.cancel();
            matroskaAudioRef.current = undefined;
            autoSelectedAudioFileRef.current = undefined;
            if (audioSourceUrlRef.current) URL.revokeObjectURL(audioSourceUrlRef.current);
            audioSourceUrlRef.current = undefined;
        };
    }, [videoFile]);

    async function selectAudioTrack(id: string) {
        const source = matroskaAudioRef.current;
        const video = videoRef.current;
        if (source && video) {
            const blob = source.select(id);
            const { timestamp, rate, resume } = pendingAudioSelectionRef.current ?? {
                timestamp: video.currentTime,
                rate: video.playbackRate,
                resume: !video.paused,
            };
            pendingAudioSelectionRef.current?.cancel();
            video.pause();
            const previousUrl = audioSourceUrlRef.current;
            const url = URL.createObjectURL(blob);
            audioSourceUrlRef.current = url;
            source.selectedTrack = id;
            await new Promise<void>((resolve, reject) => {
                const cleanup = () => {
                    video.removeEventListener('loadedmetadata', loaded);
                    video.removeEventListener('error', failed);
                    pendingAudioSelectionRef.current = undefined;
                };
                const loaded = () => {
                    cleanup();
                    video.currentTime = timestamp;
                    video.playbackRate = rate;
                    resolve();
                };
                const failed = () => {
                    cleanup();
                    reject(new Error(errorMessageFromVideo(video)));
                };
                pendingAudioSelectionRef.current = {
                    timestamp,
                    rate,
                    resume,
                    cancel: () => {
                        cleanup();
                        reject(new DOMException('Audio selection cancelled', 'AbortError'));
                    },
                };
                video.addEventListener('loadedmetadata', loaded);
                video.addEventListener('error', failed);
                video.src = url;
                if (previousUrl) URL.revokeObjectURL(previousUrl);
            });
            if (resume) await video.play();
            return;
        }
        const audioTracks = videoRef.current?.audioTracks;

        if (!audioTracks) {
            return;
        }

        for (const t of audioTracks) {
            if (t.id === id) {
                t.enabled = true;
            } else {
                t.enabled = false;
            }
        }
    }

    const handleAudioTrackSelected = useCallback(
        (id: string) => {
            void selectAudioTrack(id)
                .then(() => {
                    const source = matroskaAudioRef.current;
                    const track =
                        source?.tracks.find((track) => track.id === id) ??
                        [...(videoRef.current?.audioTracks ?? [])].find((track) => track.id === id);
                    if (track?.language && settingsRef.current.rememberEmbeddedLanguage) {
                        const embeddedLanguage =
                            embeddedTrackLanguages.find(
                                ({ name }) => name === embeddedTrackLanguageDisplayName(track.language)
                            )?.code ?? track.language;
                        if (embeddedLanguage !== settingsRef.current.embeddedLanguage)
                            onSettingsChangedRef.current({ embeddedLanguage });
                    }
                    setSelectedAudioTrack(id);
                    playerChannel.audioTrackSelected(id);
                })
                .catch((error) => {
                    if (error.name !== 'AbortError') onErrorRef.current(error);
                });
        },
        [playerChannel]
    );

    useEffect(() => {
        if (
            !settings.loadEmbeddedAudio ||
            !settings.rememberEmbeddedLanguage ||
            !sourceFile ||
            !audioTracks?.length ||
            autoSelectedAudioFileRef.current === sourceFile
        )
            return;
        if (!matroskaAudioRef.current && (videoRef.current?.audioTracks?.length ?? 0) < 2) return;
        const preferredLanguage = embeddedTrackLanguageDisplayName(settings.embeddedLanguage);
        const track = audioTracks.find(
            (track) => embeddedTrackLanguageDisplayName(track.language) === preferredLanguage
        );
        if (!track) return;
        autoSelectedAudioFileRef.current = sourceFile;
        const selected = matroskaAudioRef.current?.selectedTrack ?? selectedAudioTrack;
        if (track.id !== selected) handleAudioTrackSelected(track.id);
    }, [
        sourceFile,
        audioTracks,
        selectedAudioTrack,
        settings.loadEmbeddedAudio,
        settings.rememberEmbeddedLanguage,
        settings.embeddedLanguage,
        handleAudioTrackSelected,
    ]);

    const togglePlaybackMode = useCallback(
        (targetMode: PlayMode) => playbackEngineRef.current?.togglePlaybackMode(targetMode),
        []
    );

    useEffect(() => {
        const playbackEngine = playbackEngineRef.current;
        if (!playerChannelSubscribed || !playbackEngine) return;
        playbackEngine.settingsChanged({ ...settings, ...miscSettings });
    }, [miscSettings, playerChannelSubscribed, settings, video]);

    useEffect(() => {
        const playbackEngine = playbackEngineRef.current;
        if (!playerChannelSubscribed || !playbackEngine) return;
        playbackEngine.subtitlesChanged(subtitles);
    }, [playerChannelSubscribed, subtitles, video]);

    useEffect(() => {
        asbTrace('playback/video-player', 'Subscribing to player channel');
        playerChannel.onReady((duration, videoFileName, file) => {
            if (file) setSourceFile((current) => current ?? file);
            asbTrace('playback/video-player', 'Received player-channel ready event', {
                durationMs: duration * 1000,
                hasVideoFileName: videoFileName !== undefined,
            });
            setLengthMs(duration);
            setVideoFileName(videoFileName);
            videoFileNameRef.current = videoFileName;
            playbackEngineRef.current?.playbackPositionKeysChanged(videoFileName ? [videoFileName] : [], {
                isNewSource: true,
            });
        });

        playerChannel.onPlay(() => {
            void (async () => {
                await videoRef.current?.play();
                clock.start();
            })();
        });

        playerChannel.onPause(() => {
            videoRef.current?.pause();
            clock.stop();
        });

        playerChannel.onCurrentTime((currentTime) => {
            let actualCurrentTime = currentTime;

            if (videoRef.current) {
                decodeRecoveryAttemptedRef.current = false;
                actualCurrentTime = seekWithNudge(videoRef.current, currentTime);
            }

            if (videoRef.current?.readyState === 4) {
                playerChannel.readyState(4);
            }

            clock.setTime(actualCurrentTime * 1000, { paused: true });
        });

        playerChannel.onAudioTrackSelected(handleAudioTrackSelected);

        playerChannel.onClose(() => {
            asbTrace('playback/video-player', 'Received player-channel close command');
            playerChannel.close();
            if (!embedded) window.close();
        });

        playerChannel.onSubtitles((subtitles, _fileName, assTracks) => {
            setChannelAssTracks(assTracks ?? []);
            asbTrace('playback/video-player', 'Received subtitles from player channel', {
                subtitleCount: subtitles.length,
            });
            const offset = playbackEngineRef.current?.lastSubtitleOffset ?? 0;
            const videoSubtitles = subtitles.map((s, i) => ({
                ...s,
                start: s.originalStart + offset,
                end: s.originalEnd + offset,
                index: i,
            }));
            subtitlesRef.current = videoSubtitles;
            setChannelSubtitles(videoSubtitles);
            if (videoSubtitles.length > 0) {
                offsetRef.current = offset;
                setOffset(offset);
            }

            playerChannel.offset(offset);

            setShowSubtitles([]);
            setInvisibleSubtitles([]);
        });
        playerChannel.onSubtitlesUpdated((updatedSubtitles) => {
            asbTrace('playback/video-player', 'Received subtitle update from player channel', {
                subtitleCount: updatedSubtitles.length,
            });
            const updatedByIndex = new Map(updatedSubtitles.map((s) => [s.index, s] as const));
            if (showSubtitlesRef.current.some((s) => updatedByIndex.has(s.index))) {
                setShowSubtitles((prevShowSubtitles) =>
                    prevShowSubtitles.map((showSubtitle) => {
                        const updatedShowSubtitle = updatedByIndex.get(showSubtitle.index);
                        if (!updatedShowSubtitle) return showSubtitle;
                        return {
                            ...showSubtitle,
                            text: updatedShowSubtitle.text,
                            tokenization: updatedShowSubtitle.tokenization,
                        };
                    })
                );
            }

            if (!subtitlesRef.current.length) return;
            const allSubtitles = subtitlesRef.current.slice();
            for (const s of updatedSubtitles) {
                allSubtitles[s.index] = {
                    ...allSubtitles[s.index],
                    text: s.text,
                    tokenization: s.tokenization,
                };
            }
            subtitlesRef.current = allSubtitles;
            setChannelSubtitles(allSubtitles);
        });

        playerChannel.onPlayMode((targetMode) => togglePlaybackMode(targetMode));
        playerChannel.onHideSubtitlePlayerToggle((hidden) => setSubtitlePlayerHidden(hidden));
        playerChannel.onFullscreenToggle((fullscreen) => requestFullscreen(fullscreen));
        playerChannel.onSubtitleSettings(setSubtitleSettings);
        playerChannel.onMiscSettings(setMiscSettings);
        playerChannel.onAnkiSettings(setAnkiSettings);
        playerChannel.onOffset((offset) => {
            const playbackEngine = playbackEngineRef.current;
            if (!playbackEngine) return;
            playbackEngine.subtitleOffsetChanged(offset, { notifyPlayer: true });
        });
        playerChannel.onPlaybackRate((playbackRate) => updatePlaybackRate(playbackRate, false));
        playerChannel.onAlert((message, severity) => {
            setAlert({
                open: true,
                notifications: [{ message, severity: severity as AlertNotification['severity'] }],
            });
        });

        const onBeforeUnload = () => {
            if (!poppingInRef.current) playerChannel.close();
        };
        window.addEventListener('beforeunload', onBeforeUnload);

        setPlayerChannelSubscribed(true);
        playerChannel.playModes(playbackEngineRef.current?.playbackModes ?? new Set([PlayMode.normal]));
        asbTrace('playback/video-player', 'Player channel subscribed');
        return () => {
            asbTrace('playback/video-player', 'Closing player channel');
            window.removeEventListener('beforeunload', onBeforeUnload);
            playerChannel.close();
        };
    }, [
        clock,
        playerChannel,
        requestFullscreen,
        handleAudioTrackSelected,
        togglePlaybackMode,
        updatePlaybackRate,
        updateSubtitlesWithOffset,
        embedded,
    ]);

    const handlePlay = useCallback(() => {
        if (videoRef.current) {
            playerChannel.play();
        }
    }, [playerChannel]);

    const handlePause = useCallback(() => playerChannel.pause(), [playerChannel]);

    const handleSeek = useCallback(
        (progress: number) => {
            if (!Number.isFinite(lengthMs)) {
                return;
            }

            if (playing()) {
                clock.stop();
            }

            const time = progress * lengthMs;
            // get a screenshot of this time when hovered
            playerChannel.currentTime(time / 1000);
        },
        [lengthMs, clock, playerChannel]
    );

    const handleSeekPreview = useCallback(
        (progress: number): string | undefined => {
            if (!Number.isFinite(lengthMs)) {
                return;
            }

            if (!hiddenVideoRef.current) {
                return;
            }

            const time = progress * lengthMs;
            const video = hiddenVideoRef.current;

            const thumbnailKey = Math.floor(time / 1000 / 5);

            // cached url found, return url
            if (thumbnailsRef.current.has(thumbnailKey)) {
                return thumbnailsRef.current.get(thumbnailKey);
            }

            if (isGeneratingRef.current) return;
            // if not in cache, generate new one in the background
            isGeneratingRef.current = true;

            const onSeeked = () => {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth * 0.7;
                canvas.height = video.videoHeight * 0.7;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

                const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
                thumbnailsRef.current.set(thumbnailKey, thumbnailUrl);
                isGeneratingRef.current = false;
            };

            video.addEventListener('seeked', onSeeked, { once: true });
            video.currentTime = time / 1000;

            return;
        },
        [lengthMs]
    );

    // load or unload preview thumbnail
    useEffect(() => {
        const videoPreview = hiddenVideoRef.current;

        if (!videoPreview) return;

        if (settings.thumbnailPreview) {
            videoPreview.src = videoFile;
            videoPreview.load();
        } else {
            videoPreview.pause();
            videoPreview.removeAttribute('src');
            videoPreview.load();
        }
    }, [settings.thumbnailPreview, videoFile, hiddenVideoReady]);

    const handleSeekByTimestamp = useCallback(
        (timestampMs: number) => {
            playerChannel.currentTime(timestampMs / 1000);
        },
        [playerChannel]
    );

    useEffect(() => {
        if (seekRequest !== undefined) {
            handleSeek(seekRequest.timestamp / lengthMs);
        }
    }, [handleSeek, seekRequest, lengthMs]);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        lastMouseMovementTimestamp.current = Date.now();

        if (!containerRef.current) {
            return;
        }

        const bounds = containerRef.current.getBoundingClientRect();
        mousePositionRef.current = { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
    }, []);

    const handleMouseLeave = useCallback(() => {
        mousePositionRef.current = undefined;
    }, []);

    const handleLoadFiles = useCallback(() => {
        playerChannel.loadFiles();
    }, [playerChannel]);

    const handleLoadSubtitles = useCallback(() => {
        playerChannel.loadSubtitles();
    }, [playerChannel]);

    const updateShowingSubtitles = useCallback(
        (showingSubtitleIndexes: readonly number[], invisibleSubtitleIndexes: readonly number[]) => {
            const subtitlesForIndexes = (indexes: readonly number[]) =>
                indexes
                    .map((index) => subtitlesRef.current[index])
                    .filter(
                        (subtitle): subtitle is IndexedSubtitleModel =>
                            subtitle !== undefined && !disabledSubtitleTracksRef.current[subtitle.track]
                    )
                    .slice()
                    .sort(compareSubtitlesForDisplay);
            const showingSubtitles = subtitlesForIndexes(showingSubtitleIndexes);
            const nextInvisibleSubtitles = subtitlesForIndexes(invisibleSubtitleIndexes);
            const showingChanged = !arrayEquals(showingSubtitles, showSubtitlesRef.current);
            const invisibleChanged = !arrayEquals(nextInvisibleSubtitles, invisibleSubtitlesRef.current);
            if (!showingChanged && !invisibleChanged) return;

            if (showingChanged) {
                showSubtitlesRef.current = showingSubtitles;
                setShowSubtitles(showingSubtitles);
            }
            if (invisibleChanged) {
                invisibleSubtitlesRef.current = nextInvisibleSubtitles;
                setInvisibleSubtitles(nextInvisibleSubtitles);
            }
            if (
                showingChanged &&
                showingSubtitles.length > 0 &&
                miscSettingsRef.current.autoCopyCurrentSubtitle &&
                document.hasFocus()
            ) {
                navigator.clipboard
                    .writeText(showingSubtitles.map((subtitle) => subtitle.text).join('\n'))
                    .catch(() => {
                        // ignore
                    });
            }
        },
        []
    );

    playbackStateChangedRef.current = (state) => {
        const hiddenSubtitleIndexes = state.hiddenSubtitleIndexes ?? [];
        const visibleSubtitleIndexes = state.showingSubtitleIndexes.filter(
            (index) => !hiddenSubtitleIndexes.includes(index)
        );
        showingSubtitleIndexesRef.current = visibleSubtitleIndexes;
        const invisibleSubtitleIndexes = (state.invisibleSubtitleIndexes ?? []).filter(
            (index) => !hiddenSubtitleIndexes.includes(index)
        );
        invisibleSubtitleIndexesRef.current = invisibleSubtitleIndexes;
        updateShowingSubtitles(visibleSubtitleIndexes, invisibleSubtitleIndexes);
    };

    useEffect(() => {
        updateShowingSubtitles(showingSubtitleIndexesRef.current, invisibleSubtitleIndexesRef.current);
    }, [disabledSubtitleTracks, updateShowingSubtitles]);

    const handleOffsetChange = useCallback(
        (offset: number) => {
            playbackEngineRef.current?.subtitleOffsetChanged(offset, { notifyPlayer: true });
            playerChannel.offset(offset);
        },
        [playerChannel]
    );

    const handlePlaybackRateChange = useCallback(
        (playbackRate: number) => {
            updatePlaybackRate(playbackRate, true);
        },
        [updatePlaybackRate]
    );

    useEffect(() => {
        return keyBinder.bindSeekToSubtitle(
            (event, subtitle) => {
                event.preventDefault();
                playerChannel.currentTime(subtitle.start / 1000);
            },
            () => keyEventsDisabled() || !videoRef.current,
            () => clock.time({ maxMs: lengthMs }),
            () => subtitles,
            () => settings.seekableTracks
        );
    }, [keyBinder, playerChannel, subtitles, lengthMs, clock, settings, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindSeekToBeginningOfCurrentSubtitle(
            (event, subtitle) => {
                event.preventDefault();
                playerChannel.currentTime(subtitle.start / 1000);

                if (settings.alwaysPlayOnSubtitleRepeat) {
                    playerChannel.play();
                }
            },
            () => keyEventsDisabled() || !videoRef.current,
            () => clock.time({ maxMs: lengthMs }),
            () => subtitles,
            () => settings.seekableTracks
        );
    }, [keyBinder, playerChannel, subtitles, lengthMs, clock, settings, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindSeekBackwardOrForward(
            (event, forward) => {
                event.preventDefault();
                const timestamp = clock.time({ maxMs: lengthMs });
                const seekDuration = miscSettings.seekDuration * 1000;

                if (forward) {
                    playerChannel.currentTime(Math.min(lengthMs / 1000, (timestamp + seekDuration) / 1000));
                } else {
                    playerChannel.currentTime(Math.max(0, (timestamp - seekDuration) / 1000));
                }
            },
            () => keyEventsDisabled() || !videoRef.current
        );
    }, [keyBinder, playerChannel, lengthMs, clock, miscSettings, keyEventsDisabled]);

    const calculateSurroundingSubtitles = useCallback(
        (index: number) => {
            return surroundingSubtitles(
                subtitles,
                index,
                ankiSettings.surroundingSubtitlesCountRadius,
                ankiSettings.surroundingSubtitlesTimeRadius
            );
        },
        [subtitles, ankiSettings.surroundingSubtitlesCountRadius, ankiSettings.surroundingSubtitlesTimeRadius]
    );

    useEffect(() => {
        return keyBinder.bindAdjustOffset(
            (event, offset) => {
                event.preventDefault();
                handleOffsetChange(offset);
            },
            keyEventsDisabled,
            () => subtitles
        );
    }, [keyBinder, handleOffsetChange, subtitles, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindResetOffet((event) => {
            event.preventDefault();
            handleOffsetChange(0);
        }, keyEventsDisabled);
    }, [keyBinder, handleOffsetChange, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindAdjustPlaybackRate((event, increase) => {
            const playbackEngine = playbackEngineRef.current;
            if (!playbackEngine) return;
            event.preventDefault();

            notifyPlaybackRate(
                playbackEngine.adjustPlaybackRate(
                    increase ? miscSettings.speedChangeStep : -miscSettings.speedChangeStep
                )
            );
        }, keyEventsDisabled);
    }, [updatePlaybackRate, keyBinder, miscSettings, notifyPlaybackRate, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindToggleSubtitles((event) => {
            event.preventDefault();
            setDisplaySubtitles(!displaySubtitles);
            playbackPreferences.displaySubtitles = !displaySubtitles;
        }, keyEventsDisabled);
    }, [keyBinder, displaySubtitles, playbackPreferences, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindToggleSubtitleTrackInVideo((event, track) => {
            event.preventDefault();
            setDisabledSubtitleTracks((tracks) => {
                const newTracks = { ...tracks };
                newTracks[track] = !tracks[track];
                return newTracks;
            });
        }, keyEventsDisabled);
    }, [keyBinder, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindAdjustSubtitlePositionOffset((event, increase) => {
            const newSubtitleSettings = { ...subtitleSettings };

            event.preventDefault();
            if (increase) {
                newSubtitleSettings.subtitlePositionOffset = subtitleSettings.subtitlePositionOffset + 20;
            } else {
                newSubtitleSettings.subtitlePositionOffset = subtitleSettings.subtitlePositionOffset - 20;
            }

            onSettingsChanged(newSubtitleSettings);
            setSubtitleSettings(newSubtitleSettings);
        }, keyEventsDisabled);
    }, [keyBinder, subtitleSettings, onSettingsChanged, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindAdjustTopSubtitlePositionOffset((event, increase) => {
            const newSubtitleSettings = { ...subtitleSettings };

            event.preventDefault();
            if (increase) {
                newSubtitleSettings.topSubtitlePositionOffset = subtitleSettings.topSubtitlePositionOffset + 20;
            } else {
                newSubtitleSettings.topSubtitlePositionOffset = subtitleSettings.topSubtitlePositionOffset - 20;
            }

            onSettingsChanged(newSubtitleSettings);
            setSubtitleSettings(newSubtitleSettings);
        }, keyEventsDisabled);
    }, [keyBinder, subtitleSettings, onSettingsChanged, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindToggleSubtitleTrackInList((event, track) => {
            event.preventDefault();
            playerChannel.toggleSubtitleTrackInList(track);
        }, keyEventsDisabled);
    }, [keyBinder, playerChannel, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindUnblurTrack((event, targetTrack) => {
            event.preventDefault();
            let newSubtitleSettings = { ...subtitleSettings };

            for (let currentTrack = 0; currentTrack < trackCount; ++currentTrack) {
                const originalValue = textSubtitleSettingsForTrack(subtitleSettings, currentTrack).subtitleBlur!;
                const targetValue = currentTrack === targetTrack ? !originalValue : originalValue;
                const change = changeForTextSubtitleSetting(
                    { subtitleBlur: targetValue },
                    newSubtitleSettings,
                    currentTrack
                );
                newSubtitleSettings = { ...newSubtitleSettings, ...change };
            }

            onSettingsChanged(newSubtitleSettings);
            setSubtitleSettings(newSubtitleSettings);
        }, keyEventsDisabled);
    }, [keyBinder, subtitleSettings, trackCount, onSettingsChanged, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindOffsetToSubtitle(
            (event, offset) => {
                event.preventDefault();
                handleOffsetChange(offset);
            },
            keyEventsDisabled,
            () => clock.time({ maxMs: lengthMs }),
            () => subtitles,
            () => miscSettings.seekableTracks
        );
    }, [keyBinder, handleOffsetChange, subtitles, clock, lengthMs, miscSettings.seekableTracks, keyEventsDisabled]);

    const extractSubtitles = useCallback(() => {
        if (!subtitles || subtitles.length === 0) {
            const timestamp = clock.time({ maxMs: lengthMs });
            const end = Math.min(timestamp + 5000, lengthMs);
            const currentSubtitle = {
                text: '',
                start: timestamp,
                originalStart: timestamp,
                end: end,
                originalEnd: end,
                track: 0,
            };

            return { currentSubtitle, surroundingSubtitles: mockSurroundingSubtitles(currentSubtitle, lengthMs, 5000) };
        } else if (showSubtitlesRef.current && showSubtitlesRef.current.length > 0) {
            const currentSubtitle = showSubtitlesRef.current[0];
            return { currentSubtitle, surroundingSubtitles: calculateSurroundingSubtitles(currentSubtitle.index) };
        }

        return undefined;
    }, [subtitles, calculateSurroundingSubtitles, lengthMs, clock]);

    const mineSubtitle = useCallback(
        (
            postMineAction: PostMineAction,
            videoFileUrl: string,
            videoFileName: string,
            selectedAudioTrack: string | undefined,
            playbackRate: number,
            subtitle: SubtitleModel,
            surroundingSubtitles: SubtitleModel[],
            cardTextFieldValues: CardTextFieldValues,
            timestamp: number
        ) => {
            if (
                postMineAction === PostMineAction.mineNewJitenWords ||
                (postMineAction === PostMineAction.showAnkiDialog && settings.miningDestination === 'jiten')
            ) {
                const words = readJitenWords(subtitle, surroundingSubtitles);
                cardTextFieldValues = {
                    ...cardTextFieldValues,
                    jitenWords: words.length ? words : cardTextFieldValues?.jitenWords,
                };
            }
            switch (postMineAction) {
                case PostMineAction.showAnkiDialog:
                    playerChannel.copy(
                        subtitle,
                        surroundingSubtitles,
                        cardTextFieldValues,
                        videoFileName ?? '',
                        timestamp,
                        PostMineAction.none
                    );
                    onAnkiDialogRequest(
                        videoFileUrl,
                        videoFileName ?? '',
                        selectedAudioTrack,
                        playbackRate,
                        subtitle,
                        surroundingSubtitles,
                        cardTextFieldValues,
                        timestamp
                    );

                    if (playing()) {
                        playerChannel.pause();
                        setWasPlayingOnAnkiDialogRequest(true);
                    } else {
                        setWasPlayingOnAnkiDialogRequest(false);
                    }
                    break;
                default:
                    playerChannel.copy(
                        subtitle,
                        surroundingSubtitles,
                        cardTextFieldValues,
                        videoFileName ?? '',
                        timestamp,
                        postMineAction
                    );
            }

            setLastMinedRecord({
                videoFileUrl,
                videoFileName: videoFileName ?? '',
                selectedAudioTrack,
                playbackRate,
                subtitle,
                surroundingSubtitles,
                timestamp,
            });
        },
        [onAnkiDialogRequest, playerChannel, settings.miningDestination]
    );

    const mineCurrentSubtitle = useCallback(
        (
            postMineAction: PostMineAction,
            subtitle?: SubtitleModel,
            surroundingSubtitles?: SubtitleModel[],
            cardTextFieldValues?: CardTextFieldValues
        ) => {
            const video = videoRef.current;

            if (!video) {
                return;
            }

            const currentTimestamp = clock.time({ maxMs: lengthMs });
            let mediaTimestamp: number;

            if (subtitle === undefined || surroundingSubtitles === undefined) {
                const extracted = extractSubtitles();

                if (extracted === undefined) {
                    return;
                }

                subtitle = extracted.currentSubtitle;
                surroundingSubtitles = extracted.surroundingSubtitles;
                mediaTimestamp = currentTimestamp;
            } else if (currentTimestamp >= subtitle.start && currentTimestamp <= subtitle.end) {
                mediaTimestamp = currentTimestamp;
            } else {
                mediaTimestamp = subtitleTimestampWithDelay(subtitle, settings.streamingScreenshotDelay);
            }

            mineSubtitle(
                postMineAction,
                videoFile,
                videoFileName ?? '',
                selectedAudioTrack,
                video.playbackRate,
                subtitle,
                surroundingSubtitles,
                cardTextFieldValues ?? {},
                mediaTimestamp
            );
        },
        [
            mineSubtitle,
            extractSubtitles,
            clock,
            settings.streamingScreenshotDelay,
            selectedAudioTrack,
            videoFile,
            videoFileName,
            lengthMs,
        ]
    );

    useJitenTarget(
        useCallback(
            (target: JitenTarget) => {
                if (
                    (embedded && keyEventsDisabled()) ||
                    settings.miningDestination !== 'jiten' ||
                    !settings.jitenAutoAttach ||
                    ankiDialogOpen
                )
                    return;
                const subtitle = findJitenSubtitle(target, subtitles ?? [], clock.time({ maxMs: lengthMs }));
                if (!subtitle) return;
                mineCurrentSubtitle(
                    PostMineAction.updateLastCard,
                    subtitle,
                    calculateSurroundingSubtitles(subtitle.index),
                    {
                        jitenTarget: target,
                        miningDestination: 'jiten',
                    }
                );
            },
            [
                settings.miningDestination,
                settings.jitenAutoAttach,
                ankiDialogOpen,
                mineCurrentSubtitle,
                subtitles,
                clock,
                lengthMs,
                calculateSurroundingSubtitles,
                embedded,
                keyEventsDisabled,
            ]
        )
    );

    const toggleSelectMiningInterval = useCallback(
        (postMineAction: PostMineAction, cardTextFieldValues?: CardTextFieldValues) => {
            if (mineIntervalStartTimestamp === undefined) {
                setMineIntervalStartTimestamp(clock.time({ maxMs: lengthMs }));

                if (!playing()) {
                    playerChannel.play();
                }

                if (!isMobile) {
                    setAlert({
                        open: true,
                        notifications: [
                            {
                                message: { locKey: 'info.manualMiningIntervalPrompt', replacements: {} },
                                severity: 'info',
                            },
                        ],
                    });
                    setAlertDisableAutoHide(true);
                }
            } else {
                setAlertDisableAutoHide(false);
                setAlert((alert) => ({ ...alert, open: false }));
                const video = videoRef.current;

                if (!video) {
                    return;
                }

                const endTimestamp = clock.time({ maxMs: lengthMs });

                if (endTimestamp > mineIntervalStartTimestamp) {
                    let currentSubtitle: SubtitleModel = {
                        text: '',
                        start: mineIntervalStartTimestamp,
                        originalStart: mineIntervalStartTimestamp,
                        end: endTimestamp,
                        originalEnd: endTimestamp,
                        track: 0,
                    };
                    let surroundingSubtitles: SubtitleModel[];

                    if (subtitles.length === 0) {
                        surroundingSubtitles = mockSurroundingSubtitles(currentSubtitle, lengthMs, 5000);
                    } else {
                        const calculated = surroundingSubtitlesAroundInterval(
                            subtitles,
                            mineIntervalStartTimestamp,
                            endTimestamp,
                            settings.surroundingSubtitlesCountRadius,
                            settings.surroundingSubtitlesTimeRadius
                        );
                        currentSubtitle = {
                            ...currentSubtitle,
                            text: calculated.subtitle?.text ?? '',
                        };
                        surroundingSubtitles = calculated.surroundingSubtitles ?? [];
                    }

                    mineSubtitle(
                        postMineAction,
                        videoFile,
                        videoFileName ?? '',
                        selectedAudioTrack,
                        video.playbackRate,
                        currentSubtitle,
                        surroundingSubtitles,
                        cardTextFieldValues ?? {},
                        mineIntervalStartTimestamp
                    );
                }

                setMineIntervalStartTimestamp(undefined);
            }
        },
        [
            mineSubtitle,
            playerChannel,
            mineIntervalStartTimestamp,
            clock,
            lengthMs,
            selectedAudioTrack,
            videoFile,
            videoFileName,
            subtitles,
            settings.surroundingSubtitlesCountRadius,
            settings.surroundingSubtitlesTimeRadius,
        ]
    );

    const inferAndExecuteMiningBehavior = useCallback(
        (
            postMineAction: PostMineAction,
            subtitle?: SubtitleModel,
            surroundingSubtitles?: SubtitleModel[],
            cardTextFieldValues?: CardTextFieldValues
        ) => {
            if (!subtitle && !surroundingSubtitles && subtitles.length === 0) {
                toggleSelectMiningInterval(postMineAction, cardTextFieldValues);
            } else {
                if (mineIntervalStartTimestamp !== undefined) {
                    // Edge case: user started manually recording but are now using an "automatic" mining shortcut
                    // Cancel the "recording" operation
                    setAlertDisableAutoHide(false);
                    setAlert((alert) => ({ ...alert, open: false }));
                    setMineIntervalStartTimestamp(undefined);
                }

                mineCurrentSubtitle(postMineAction, subtitle, surroundingSubtitles, cardTextFieldValues);
            }
        },
        [mineCurrentSubtitle, toggleSelectMiningInterval, mineIntervalStartTimestamp, subtitles]
    );

    useEffect(() => {
        return playerChannel.onCopy(inferAndExecuteMiningBehavior);
    }, [playerChannel, inferAndExecuteMiningBehavior]);

    useEffect(
        () =>
            keyBinder.bindMineNewJitenWords(
                (event) => {
                    event.preventDefault();
                    inferAndExecuteMiningBehavior(PostMineAction.mineNewJitenWords);
                },
                () => keyEventsDisabled() || ankiDialogOpen
            ),
        [keyBinder, inferAndExecuteMiningBehavior, ankiDialogOpen, keyEventsDisabled]
    );

    useEffect(() => {
        return keyBinder.bindAnkiExport((event) => {
            event.preventDefault();
            event.stopPropagation();
            inferAndExecuteMiningBehavior(PostMineAction.showAnkiDialog);
        }, keyEventsDisabled);
    }, [inferAndExecuteMiningBehavior, keyBinder, keyEventsDisabled]);

    useEffect(() => {
        return miningContext.onEvent('stopped-mining', () => {
            switch (miscSettings.postMiningPlaybackState) {
                case PostMinePlayback.play:
                    playerChannel.play();
                    break;
                case PostMinePlayback.pause:
                    playerChannel.pause();
                    break;
                case PostMinePlayback.remember:
                    if (wasPlayingOnAnkiDialogRequest) {
                        playerChannel.play();
                    }
                    break;
            }
        });
    }, [miningContext, wasPlayingOnAnkiDialogRequest, miscSettings, playerChannel]);

    useEffect(() => {
        return keyBinder.bindUpdateLastCard((event) => {
            event.preventDefault();
            event.stopPropagation();
            inferAndExecuteMiningBehavior(PostMineAction.updateLastCard);
        }, keyEventsDisabled);
    }, [inferAndExecuteMiningBehavior, keyBinder, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindTakeScreenshot(
            (event) => {
                event.preventDefault();

                if (ankiDialogOpen) {
                    onAnkiDialogRewind();
                } else if (lastMinedRecord) {
                    const currentTimestamp = clock.time({ maxMs: lengthMs });
                    mineSubtitle(
                        PostMineAction.showAnkiDialog,
                        lastMinedRecord.videoFileUrl,
                        lastMinedRecord.videoFileName,
                        lastMinedRecord.selectedAudioTrack,
                        lastMinedRecord.playbackRate,
                        lastMinedRecord.subtitle,
                        lastMinedRecord.surroundingSubtitles,
                        {},
                        currentTimestamp
                    );
                }
            },
            () => keyEventsDisabled() && !ankiDialogOpen
        );
    }, [
        clock,
        lengthMs,
        keyBinder,
        lastMinedRecord,
        mineSubtitle,
        popOut,
        ankiDialogOpen,
        onAnkiDialogRewind,
        keyEventsDisabled,
    ]);

    useEffect(() => {
        return keyBinder.bindToggleRecording((event) => {
            event.preventDefault();
            toggleSelectMiningInterval(PostMineAction.showAnkiDialog);
        }, keyEventsDisabled);
    }, [keyBinder, toggleSelectMiningInterval, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindCopy(
            (event) => {
                event.preventDefault();
                inferAndExecuteMiningBehavior(settings.clickToMineDefaultAction);
            },
            keyEventsDisabled,
            () => {
                const extracted = extractSubtitles();

                if (extracted === undefined) {
                    return undefined;
                }

                return extracted.currentSubtitle;
            }
        );
    }, [
        extractSubtitles,
        inferAndExecuteMiningBehavior,
        keyBinder,
        keyEventsDisabled,
        settings.clickToMineDefaultAction,
    ]);

    useEffect(() => {
        return keyBinder.bindPlay((event) => {
            event.preventDefault();

            if (playing()) {
                playerChannel.pause();
            } else {
                playerChannel.play();
            }
        }, keyEventsDisabled);
    }, [keyBinder, playerChannel, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindAutoPause((event) => {
            event.preventDefault();
            togglePlaybackMode(PlayMode.autoPause);
        }, keyEventsDisabled);
    }, [keyBinder, togglePlaybackMode, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindCondensedPlayback((event) => {
            event.preventDefault();
            togglePlaybackMode(PlayMode.condensed);
        }, keyEventsDisabled);
    }, [keyBinder, togglePlaybackMode, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindFastForwardPlayback((event) => {
            event.preventDefault();
            togglePlaybackMode(PlayMode.fastForward);
        }, keyEventsDisabled);
    }, [keyBinder, togglePlaybackMode, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindToggleRepeat((event) => {
            event.preventDefault();
            togglePlaybackMode(PlayMode.repeat);
        }, keyEventsDisabled);
    }, [keyBinder, togglePlaybackMode, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindCycleAutoPauseResumeMode(
            (event) => {
                event.preventDefault();
                const notification = playbackEngineRef.current?.cycleAutoPauseResumeMode();
                if (notification === undefined) return;
                setAlert({
                    open: true,
                    notifications: [
                        {
                            key: notification.key,
                            message: {
                                locKey: notification.locKey,
                                replacements: { value: t(notification.valueLocKey) },
                            },
                            severity: 'info',
                        },
                    ],
                });
            },
            () => keyEventsDisabled() || subtitles.length === 0
        );
    }, [keyBinder, subtitles.length, t, keyEventsDisabled]);

    const handleSubtitleStudyToggle = useCallback(
        (mode: 'blur' | 'hide') => {
            const key = mode === 'blur' ? 'subtitleStudyBlurEnabled' : 'subtitleStudyHideEnabled';
            onSettingsChanged({ [key]: !settings[key] });
        },
        [onSettingsChanged, settings]
    );

    useEffect(() => {
        return keyBinder.bindToggleSubtitleStudy(
            (event, mode) => {
                event.preventDefault();
                handleSubtitleStudyToggle(mode);
            },
            () => keyEventsDisabled()
        );
    }, [keyBinder, handleSubtitleStudyToggle, keyEventsDisabled]);

    useEffect(() => {
        return keyBinder.bindToggleSubtitleVisibility(
            (event) => {
                event.preventDefault();
                const notification = playbackEngineRef.current?.toggleSubtitleVisibility();
                if (notification === undefined) return;
                setAlert({
                    open: true,
                    notifications: [
                        {
                            key: notification.key,
                            message: {
                                locKey: notification.locKey,
                                replacements: { value: t(notification.valueLocKey) },
                            },
                            severity: 'info',
                        },
                    ],
                });
            },
            () => keyEventsDisabled() || subtitles.length === 0
        );
    }, [keyBinder, subtitles.length, t, keyEventsDisabled]);

    const handleSubtitlesToggle = useCallback(() => {
        setDisplaySubtitles(!displaySubtitles);
        playbackPreferences.displaySubtitles = !displaySubtitles;
    }, [displaySubtitles, playbackPreferences]);

    const handleFullscreenToggle = useCallback(() => {
        requestFullscreen(!fullscreen);
    }, [fullscreen, requestFullscreen]);

    const handleVolumeChange = useCallback((volume: number) => {
        requestedVolume.current = volume;
        const element = videoRef.current;
        if (!element) return;
        try {
            let graph = volumeGraph.current;
            if (volume > 1 && graph?.element !== element) {
                graph?.source.disconnect();
                graph?.gain.disconnect();
                void graph?.context.close();
                const context = new AudioContext();
                const source = context.createMediaElementSource(element);
                const gain = context.createGain();
                source.connect(gain);
                gain.connect(context.destination);
                graph = { context, source, gain, element };
                volumeGraph.current = graph;
            }
            element.volume = Math.min(1, volume);
            if (graph) {
                graph.gain.gain.value = Math.max(1, volume);
                if (graph.context.state === 'suspended') {
                    void graph.context.resume().catch((error) => asbError('playback/audio', error));
                }
            }
        } catch (error) {
            asbError('playback/audio', error);
            setAlert({ open: true, notifications: [{ severity: 'error', message: String(error) }] });
        }
    }, []);

    useEffect(() => {
        if (video) handleVolumeChange(requestedVolume.current);
    }, [video, handleVolumeChange]);

    const handlePopOutToggle = useCallback(() => {
        playerChannel.popOutToggle();
        if (popOut) {
            poppingInRef.current = true;
            window.close();
        }
    }, [playerChannel, popOut]);

    const handlePlayMode = useCallback((targetMode: PlayMode) => togglePlaybackMode(targetMode), [togglePlaybackMode]);

    const handleHideSubtitlePlayerToggle = useCallback(() => {
        playerChannel.hideSubtitlePlayerToggle();
    }, [playerChannel]);

    const handleSubtitleAlignment = useCallback(
        (alignment: SubtitleAlignment) => {
            let change: Partial<SubtitleSettings> = {};

            for (let track = 0; track < subtitleAlignments.length; ++track) {
                change = {
                    ...change,
                    ...changeForTextSubtitleSetting({ subtitleAlignment: alignment }, subtitleSettings, track),
                };
            }

            const newSubtitleSettings = { ...subtitleSettings, ...change };
            setSubtitleAlignments([alignment]);
            onSettingsChanged(newSubtitleSettings);
            setSubtitleSettings(newSubtitleSettings);
        },
        [onSettingsChanged, subtitleSettings, subtitleAlignments]
    );

    const handleClick = useCallback(() => {
        if (playing()) {
            playerChannel.pause();
        } else {
            playerChannel.play();
        }
    }, [playerChannel]);

    const handleDoubleClick = useCallback(() => handleFullscreenToggle(), [handleFullscreenToggle]);

    useEffect(() => {
        if (isMobile) {
            return;
        }

        const interval = setInterval(() => {
            if (Date.now() - lastMouseMovementTimestamp.current > 300) {
                if (showCursor) {
                    setShowCursor(false);
                }
            } else if (!showCursor) {
                setShowCursor(true);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [showCursor]);

    const handleAlertClosed = useCallback(() => {
        setAlertDisableAutoHide(false);
        setAlert((alert) => ({ ...alert, open: false }));
    }, []);
    const trackStyles = useSubtitleStyles(subtitleSettings, trackCount ?? 1);

    const getSubtitleHtml = useCallback(
        (subtitle: IndexedSubtitleModel) =>
            showingSubtitleHtml(
                subtitle,
                videoRef,
                trackStyles[subtitle.track]?.styleString ?? trackStyles[0]?.styleString ?? '',
                trackStyles[subtitle.track]?.classes ?? trackStyles[0]?.classes ?? '',
                subtitleSettings.imageBasedSubtitleScaleFactor
            ),
        [trackStyles, subtitleSettings.imageBasedSubtitleScaleFactor]
    );

    useJitenSubtitleSource(subtitles, !embedded, settings.assSubtitleStyles);

    const handleSwipe = useCallback(
        (direction: Direction) => {
            const subtitle = adjacentSubtitle(
                direction === 'right',
                clock.time({ maxMs: lengthMs }),
                subtitles,
                miscSettings.seekableTracks
            );
            if (subtitle) {
                playerChannel.currentTime(subtitle.start / 1000);
            }
        },
        [clock, lengthMs, subtitles, playerChannel, miscSettings.seekableTracks]
    );

    useSwipe({
        onSwipe: handleSwipe,
        distance: 50,
        ms: 500,
    });

    const isPausedDueToHoverRef = useRef<boolean>(undefined);

    const handleSubtitleMouseOver = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (miscSettings.pauseOnHoverMode !== PauseOnHoverMode.disabled && videoRef.current?.paused === false) {
                hoveredSubtitleRef.current =
                    (event.target as HTMLElement).closest<HTMLElement>(`[${ASB_SUBTITLE_INDEX_ATTRIBUTE}]`) ??
                    event.currentTarget;
                hoveredSubtitleBoundsRef.current = hoveredSubtitleRef.current.getBoundingClientRect();
                playerChannel.pause();
                isPausedDueToHoverRef.current = true;
            }
        },
        [miscSettings.pauseOnHoverMode, playerChannel]
    );

    const handleSubtitleMouseOut = useCallback(() => {}, []);

    const handleVideoMouseOver = useCallback(
        (e: React.MouseEvent<HTMLVideoElement>) => {
            if (miscSettings.pauseOnHoverMode === PauseOnHoverMode.inAndOut && isPausedDueToHoverRef.current) {
                const initial = hoveredSubtitleBoundsRef.current!;
                const element = hoveredSubtitleRef.current!;
                const current = element.isConnected ? element.getBoundingClientRect() : initial;
                if (
                    e.clientX >= Math.min(initial.left, current.left) &&
                    e.clientX <= Math.max(initial.right, current.right) &&
                    e.clientY >= Math.min(initial.top, current.top) &&
                    e.clientY <= Math.max(initial.bottom, current.bottom)
                )
                    return;
                playerChannel.play();
                isPausedDueToHoverRef.current = false;
            }
        },
        [miscSettings.pauseOnHoverMode, playerChannel]
    );

    const { lastControlType, setLastControlType } = useLastScrollableControlType({
        isMobile,
        fetchLastControlType,
        saveLastControlType,
    });

    // If the video player is taking up the entire screen, then the subtitle player isn't showing
    // This code assumes some behavior in Player, namely that the subtitle player is automatically hidden
    // (and therefore the VideoPlayer takes up all the space) when there isn't enough room for the subtitle player
    // to be displayed.
    const notEnoughRoomForSubtitlePlayer =
        !subtitlePlayerHidden && (containerRef.current?.clientWidth ?? 0) >= document.body.clientWidth;

    const elementForSubtitle = (subtitle: IndexedSubtitleModel, visible: boolean) => (
        <ShowingSubtitle
            key={subtitle.index}
            subtitle={subtitle}
            visible={visible}
            renderHtml={getSubtitleHtml}
            onMouseOver={handleSubtitleMouseOver}
            onMouseOut={handleSubtitleMouseOut}
        />
    );

    const subtitleAlignment = (subtitle: IndexedSubtitleModel) =>
        subtitle.alignment ?? subtitleAlignments[subtitle.track] ?? subtitleAlignments[0];
    const subtitleElementsWithAlignment = (alignment: SubtitleAlignment) => {
        const showing = showSubtitles.filter(
            (subtitle) =>
                subtitleAlignment(subtitle) === alignment &&
                !styledAssTracks.some((track) => track.track === subtitle.track && subtitle.assText)
        );
        const invisible = invisibleSubtitles.filter(
            (subtitle) =>
                subtitleAlignment(subtitle) === alignment &&
                !styledAssTracks.some((track) => track.track === subtitle.track && subtitle.assText)
        );
        return mapSubtitlesForDisplay(showing, invisible, elementForSubtitle);
    };
    const topSubtitleElements = displaySubtitles ? subtitleElementsWithAlignment('top') : [];
    const bottomSubtitleElements = displaySubtitles ? subtitleElementsWithAlignment('bottom') : [];
    const mobileOverlayModel = () => {
        if (!settings.showFloatingControls) {
            return undefined;
        }

        const timestamp = clock.time({ maxMs: lengthMs });

        return {
            offset,
            playbackRate: videoRef.current?.playbackRate ?? 1,
            emptySubtitleTrack: subtitles.length === 0,
            recordingEnabled: true,
            recording: mineIntervalStartTimestamp !== undefined,
            previousSubtitleTimestamp:
                adjacentSubtitle(false, timestamp, subtitles, miscSettings.seekableTracks)?.originalStart ?? undefined,
            nextSubtitleTimestamp:
                adjacentSubtitle(true, timestamp, subtitles, miscSettings.seekableTracks)?.originalStart ?? undefined,
            currentTimestamp: timestamp,
            postMineAction: settings.clickToMineDefaultAction,
            subtitleDisplaying: showSubtitles.length > 0,
            subtitlesAreVisible: displaySubtitles,
            playModes: Array.from(playModes),
            themeType: settings.themeType,
        };
    };
    const subtitleControlInsets = !playing() ? controlInsets : undefined;
    const alertAnchor = subtitleAlignments[0] === 'top' ? 'bottom' : 'top';
    const resumePlaybackPrompt = useMemo(
        () => (
            <>
                {t('info.resumePlaybackPrompt', {
                    time: timeDurationDisplay(pendingPlaybackPosition ?? 0, pendingPlaybackPosition ?? 0, false),
                })}
                <Button
                    size="small"
                    color="inherit"
                    style={{ pointerEvents: 'auto', marginLeft: 12 }}
                    onClick={() => void playbackEngineRef.current?.resumePlaybackPosition()}
                >
                    {t('info.resumePlaybackButton')}
                </Button>
            </>
        ),
        [pendingPlaybackPosition, t]
    );

    if (!playerChannelSubscribed || lastControlType === undefined) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            style={embedded ? { height: '100%' } : undefined}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className={`${classes.root} asbplayer-token-container`}
            tabIndex={-1}
        >
            <Alert
                open={resumePlaybackSnackbar.open}
                onClose={resumePlaybackSnackbar.close}
                onMouseEnter={resumePlaybackSnackbar.onMouseEnter}
                onMouseLeave={resumePlaybackSnackbar.onMouseLeave}
                autoHideDuration={0}
                disableAutoHide={true}
                useAppLogo={false}
                severity="info"
                anchor="bottom"
            >
                {resumePlaybackPrompt}
            </Alert>
            <Alert
                open={alert.open}
                disableAutoHide={alertDisableAutoHide}
                onClose={handleAlertClosed}
                notifications={alert.notifications}
                anchor={alertAnchor}
                useAppLogo={false}
            />
            <video
                preload="auto"
                onPlay={() => {
                    const context = volumeGraph.current?.context;
                    if (context?.state === 'suspended') {
                        void context.resume().catch((error) => asbError('playback/audio', error));
                    }
                }}
                controls={false}
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
                className={showCursor ? classes.video : `${classes.cursorHidden} ${classes.video}`}
                ref={videoRefCallback}
                src={videoFile}
                onMouseOver={handleVideoMouseOver}
                onMouseMove={handleVideoMouseOver}
            />
            {/* Optional blur mask overlay; constrained to the video bounds within the player container */}
            {blurOverlayVisible && <BlurOverlay anchorRef={containerRef} containerRef={videoRef} />}
            {/* ASS.js captures the video dimensions at initialization, so wait for metadata. */}
            {video &&
                videoWidth !== undefined &&
                videoHeight !== undefined &&
                styledAssTracks.map((track, index) => (
                    <AssSubtitles
                        key={index}
                        video={video}
                        track={track}
                        subtitles={subtitles}
                        showingSubtitles={showSubtitles}
                        offset={offset}
                        visible={displaySubtitles && !disabledSubtitleTracks[track.track]}
                        controlInsets={subtitleControlInsets}
                        className={trackStyles[track.track]?.classes}
                        onMouseOver={handleSubtitleMouseOver}
                        onMouseOut={handleSubtitleMouseOut}
                        onError={handleAssError}
                    />
                ))}
            {/* this video is for getting the seek preview below */}
            {miscSettings.thumbnailPreview && (
                <video
                    src={videoFile}
                    muted
                    preload="none"
                    autoPlay={false}
                    style={{ position: 'absolute', left: '-9999px' }}
                    ref={hiddenVideoRefCallback}
                />
            )}
            {topSubtitleElements.length > 0 && (
                <SubtitleContainer
                    alignment={'top'}
                    subtitleSettings={subtitleSettings}
                    baseOffset={
                        subtitleControlInsets
                            ? Math.max(0, subtitleControlInsets.top + 8 - subtitleSettings.topSubtitlePositionOffset)
                            : 0
                    }
                    subtitleZIndex={settings.subtitleAboveThumbnail}
                >
                    {topSubtitleElements}
                </SubtitleContainer>
            )}
            {bottomSubtitleElements.length > 0 && (
                <SubtitleContainer
                    alignment={'bottom'}
                    subtitleSettings={subtitleSettings}
                    baseOffset={
                        subtitleControlInsets
                            ? Math.max(0, subtitleControlInsets.bottom + 8 - subtitleSettings.subtitlePositionOffset)
                            : 0
                    }
                    subtitleZIndex={settings.subtitleAboveThumbnail}
                >
                    {bottomSubtitleElements}
                </SubtitleContainer>
            )}
            <Controls
                onControlInsetsChange={setControlInsets}
                onOpenCopyHistory={onOpenCopyHistory}
                topControls={topControls}
                floatingControls={() => (
                    <MobileVideoOverlay
                        model={mobileOverlayModel()}
                        className={classes.mobileOverlay}
                        anchor="bottom"
                        tooltipsEnabled={true}
                        initialControlType={lastControlType}
                        flexDirection="row"
                        onScrollToControlType={setLastControlType}
                        onMineSubtitle={
                            subtitles.length > 0 ||
                            settings.showManualRecordingButton ||
                            mineIntervalStartTimestamp !== undefined
                                ? () => inferAndExecuteMiningBehavior(settings.clickToMineDefaultAction)
                                : undefined
                        }
                        onOffset={handleOffsetChange}
                        onPlaybackRate={handlePlaybackRateChange}
                        onSeek={handleSeekByTimestamp}
                    />
                )}
                subtitleExtractionProgress={subtitleExtractionProgress}
                onCancelSubtitleExtraction={onCancelSubtitleExtraction}
                videoWidth={videoWidth}
                videoHeight={videoHeight}
                mousePositionRef={mousePositionRef}
                clock={clock}
                length={lengthMs}
                audioTracks={audioTracks}
                selectedAudioTrack={selectedAudioTrack}
                subtitlesToggle={subtitles && subtitles.length > 0}
                subtitlesEnabled={displaySubtitles}
                offsetEnabled={true}
                offset={offset}
                playbackRate={videoRef.current?.playbackRate ?? 1}
                playbackRateEnabled={true}
                fullscreenEnabled={true}
                fullscreen={fullscreen}
                popOut={popOut}
                volumeEnabled={true}
                popOutEnabled={!isMobile}
                playModeEnabled={subtitles && subtitles.length > 0}
                playModes={playModes}
                subtitleStudyBlurEnabled={settings.subtitleStudyBlurEnabled}
                subtitleStudyHideEnabled={settings.subtitleStudyHideEnabled}
                onSubtitleStudyToggle={handleSubtitleStudyToggle}
                previewEnabled={settings.thumbnailPreview}
                hideSubtitlePlayerToggleEnabled={
                    subtitles?.length > 0 && !popOut && !fullscreen && !notEnoughRoomForSubtitlePlayer
                }
                subtitlePlayerHidden={subtitlePlayerHidden}
                onPlay={handlePlay}
                onPause={handlePause}
                onSeek={handleSeek}
                onSeekPreview={handleSeekPreview}
                onAudioTrackSelected={handleAudioTrackSelected}
                onSubtitlesToggle={handleSubtitlesToggle}
                onFullscreenToggle={handleFullscreenToggle}
                onVolumeChange={handleVolumeChange}
                onOffsetChange={handleOffsetChange}
                onPlaybackRateChange={handlePlaybackRateChange}
                onPopOutToggle={handlePopOutToggle}
                onPlayMode={handlePlayMode}
                onHideSubtitlePlayerToggle={handleHideSubtitlePlayerToggle}
                playbackPreferences={playbackPreferences}
                showOnMouseMovement={false}
                subtitleAlignment={subtitleAlignments[0]}
                subtitleAlignmentEnabled={subtitleAlignments.length === 1}
                onSubtitleAlignment={handleSubtitleAlignment}
                onLoadFiles={popOut ? undefined : handleLoadFiles}
                onLoadSubtitles={popOut ? undefined : handleLoadSubtitles}
                blurOverlayEnabled={blurOverlayVisible}
                onBlurOverlayToggle={handleBlurOverlayToggle}
                timestampPreviewEnabled={!isMobile}
            />
        </div>
    );
}

export default React.memo(VideoPlayer);
